import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Option, FormLabel, FormHelperText, Box } from '@mui/joy';

/**
 * Shipment / piece status selector.
 *
 * @param {boolean} [includePartialStates=false] - When true, also lists the
 *   new rollup-only `partially_shipped` / `partially_delivered` values
 *   (FRONTEND_INTEGRATION_GUIDE §9). Use this for filter dropdowns; keep it
 *   off for edit forms because manual writes of the partial values would
 *   conflict with the auto-rollup.
 */
export default function StatusOptionList({
    label,
    required,
    status,
    setStatus,
    showAll = false,
    allowEmpty = false,
    disabled = false,
    errorMessage,
    includePartialStates = false,
}) {
    const { t } = useTranslation('ui');
    const resolvedLabel = label ?? t('widgets.shipmentStatus');
    const selectValue = allowEmpty
        ? (status ?? '')
        : (status && String(status).trim() !== '' ? status : null);

    return (
        <Box sx={{ marginBottom: 2 }}>
            <FormLabel
                sx={{
                    fontWeight: 600,
                    color: errorMessage ? 'danger.500' : '#555',
                    marginBottom: '6px',
                    fontSize: '14px',
                }}
            >
                {required ? `${resolvedLabel} *` : resolvedLabel}
            </FormLabel>
            <Select
                value={selectValue}
                onChange={(e, newValue) => setStatus(newValue ?? '')}
                required={required}
                disabled={disabled}
                placeholder={t('widgets.selectStatus')}
                color={errorMessage ? 'danger' : 'neutral'}
                variant={errorMessage ? 'outlined' : 'plain'}
                sx={{
                    backgroundColor: '#f7f9fc',
                    borderRadius: '8px',
                    padding: '8px',
                    boxShadow: errorMessage
                        ? '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)'
                        : '0 2px 4px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                        borderColor: errorMessage ? undefined : '#5f09c2',
                    },
                    '&:focus-within': {
                        borderColor: errorMessage ? undefined : '#5f09c2',
                        outline: 'none',
                        boxShadow: errorMessage
                            ? '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)'
                            : '0 0 6px rgba(0, 123, 255, 0.5)',
                    },
                    '&[disabled]': {
                        backgroundColor: '#e9ecef',
                        color: '#6c757d',
                        cursor: 'not-allowed',
                    },
                }}
            >
                {showAll && <Option value="">{t('actions.all')}</Option>}
                {allowEmpty && !showAll && <Option value="">—</Option>}
                <Option value="shipping soon">{t('widgets.shipmentStatusShippingSoon')}</Option>
                <Option value="shipped">{t('widgets.shipmentStatusShipped')}</Option>
                {includePartialStates && (
                    <Option value="partially_shipped">{t('shipmentPieces.status_partiallyShipped')}</Option>
                )}
                <Option value="arrived">{t('widgets.shipmentStatusArrived')}</Option>
                <Option value="out for delivery">{t('widgets.shipmentStatusOutForDelivery')}</Option>
                {includePartialStates && (
                    <Option value="partially_delivered">{t('shipmentPieces.status_partiallyDelivered')}</Option>
                )}
                <Option value="delivered">{t('widgets.shipmentStatusDelivered')}</Option>
            </Select>
            {errorMessage ? (
                <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{errorMessage}</FormHelperText>
            ) : null}
        </Box>
    );
}
